import React from 'react';
import { Box, Typography } from '@mui/material';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { flowComponentRegistry } from '../FlowBuilder/flow-components';
import ErrorBoundary from '../common/ErrorBoundary';

const HEADER_H = 34;
const SCALE = 0.6;
const MAX_COMPONENTS = 6;

const WHOLE_DATA_REF = /^\$\{data\.([A-Za-z0-9_]+)\}$/;
/** Any reference a Flow JSON string can carry: screen data, this screen's form, another's. */
const ANY_REF = /\$\{\s*(?:data\.([A-Za-z0-9_]+)|form\.([A-Za-z0-9_]+)|screen\.[A-Za-z0-9_]+\.form\.([A-Za-z0-9_]+))\s*\}/g;
/** Properties whose value is a list; an empty one previews better than a leftover reference. */
const LIST_PROPERTIES = new Set(['data-source']);

/** "chosen_date" → "[chosen date]": obviously a placeholder, and it says what goes there. */
const placeholderFor = (name) => `[${String(name).replace(/[_-]+/g, ' ').trim()}]`;

/**
 * Show what the customer will see, never the code that fetches it.
 *
 * Three things can stand in a Flow JSON string, and a preview cannot resolve any of them: a
 * booking screen's options come from FlowGate's data-exchange endpoint at runtime, and a form
 * reference is filled in by WhatsApp on the device. So the preview follows one rule, show the
 * value if the screen declares an example for it (every dynamic screen carries an `__example__`
 * for exactly this, and it is what Meta's own builder previews with), otherwise show a
 * placeholder that reads as one. Printing "${data.chosen_date}" at somebody makes a working
 * screen look broken, which is what it did until 2026-09-20.
 */
const withExamples = (child, screenData) => {
  let filled = child;
  Object.entries(child).forEach(([key, value]) => {
    if (typeof value !== 'string' || !value.includes('${')) return;

    // A whole-string reference is a value, not a sentence: a list reaching a Dropdown, an
    // object reaching a payload. It takes the example as it is.
    const whole = WHOLE_DATA_REF.exec(value);
    if (whole) {
      const example = screenData?.[whole[1]]?.__example__;
      if (example !== undefined) {
        filled = { ...filled, [key]: example };
      } else if (LIST_PROPERTIES.has(key)) {
        filled = { ...filled, [key]: [] };
      } else {
        filled = { ...filled, [key]: placeholderFor(whole[1]) };
      }
      return;
    }

    const substituted = value.replace(ANY_REF, (reference, dataName, formName, screenFormName) => {
      const example = dataName ? screenData?.[dataName]?.__example__ : undefined;
      // An array dropped into the middle of a sentence is noise; name it instead.
      if (example !== undefined && typeof example !== 'object') return String(example);
      return placeholderFor(dataName || formName || screenFormName);
    });
    if (substituted !== value) filled = { ...filled, [key]: substituted };
  });
  return filled;
};

const toItem = (screenData) => (child, i) =>
  ({ id: `thumb-${i}`, type: child.type, properties: withExamples(child, screenData) });

// WhatsApp Flow JSON nests a screen's components under layout.children. The component registry's
// renderPreview expects the builder's { type, properties } shape, and a child's own fields ARE its
// properties, so we map each child straight across (same as useFlowBuilder's load path).
//
// The compiler appends a Footer as the LAST child of every screen ("Continue" mid-flow, "Submit"
// on the terminal one), it is the one component a preview must never silently drop, since a form
// with no button on it reads as broken. The 'card' variant (the 150px flows-list thumbnail) still
// caps how much it shows, but always keeps the Footer by trading away a content component for it
// rather than truncating blindly. The 'full' variant (the assistant's 288px preview) shows every
// component, the point of that preview is to see exactly what was built.
/**
 * The components a customer sees when the screen first opens. Nothing is answered yet, so an If
 * that tests an answer shows its `else` branch and a Switch on an answer shows no case, which is
 * what Meta's preview showed for the parity flows (2026-10-10). Both used to draw nothing at all,
 * so a branch's fields were missing from the preview whichever way the customer went.
 */
const asOpened = (children) => (Array.isArray(children) ? children : []).flatMap((child) => {
  if (!child || typeof child !== 'object') return [];
  if (child.type === 'If') return asOpened(child.else);
  if (child.type === 'Switch') return [];
  return [child];
});

/** Whether a component needs an answer before the screen's button will go. */
const needsAnswer = (child) => {
  if (child.visible === false) return false;
  const required = child.required;
  if (required && typeof required === 'object') return Object.values(required).some(Boolean);
  if (required) return true;
  return Number(child['min-uploaded-photos'] || child['min-uploaded-documents'] || 0) > 0;
};

const screenComponents = (flowJson, index, variant) => {
  const screen = flowJson?.screens?.[index];
  const children = asOpened(screen?.layout?.children);

  const asItem = toItem(screen?.data);

  const canSubmit = !children.some(needsAnswer);

  if (variant === 'full') {
    return { title: screen?.title, components: children.map(asItem), canSubmit };
  }

  const lastIndex = children.length - 1;
  const hasFooter = lastIndex >= 0 && children[lastIndex]?.type === 'Footer';
  const footer = hasFooter ? children[lastIndex] : null;
  const rest = hasFooter ? children.slice(0, lastIndex) : children;
  const capped = rest.slice(0, footer ? MAX_COMPONENTS - 1 : MAX_COMPONENTS);
  const shown = footer ? [...capped, footer] : capped;
  return { title: screen?.title, components: shown.map(asItem), canSubmit };
};

const EmptyState = ({ label }) => (
  <Box
    sx={{
      position: 'absolute',
      inset: `${HEADER_H}px 0 0 0`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5,
      bgcolor: '#fff',
      color: 'text.disabled',
    }}
  >
    <WidgetsOutlinedIcon fontSize="small" />
    <Typography variant="caption">{label}</Typography>
  </Box>
);

// Scaled with `zoom`, not `transform: scale()`. A transform only changes how the content is
// painted; its layout box stays full size, so the scrollable preview scrolled sideways by the
// unscaled width and kept scrolling far past the Footer by the unscaled height (2026-09-27).
// `zoom` scales the layout too, so the scroll area is exactly as big as what you see.
const PreviewBody = ({ components, scrollable, canSubmit }) => (
  <Box
    sx={{
      position: 'absolute',
      inset: `${HEADER_H}px 0 0 0`,
      overflowX: 'hidden',
      overflowY: scrollable ? 'auto' : 'hidden',
      bgcolor: '#fff',
    }}
  >
    <Box
      data-testid="thumbnail-body"
      sx={{
        zoom: SCALE,
        p: 1.25,
        // The flows-list card is a picture; the assistant's full preview is there to be tried.
        pointerEvents: scrollable ? 'auto' : 'none',
      }}
    >
      {components.map((c) => {
        const fc = flowComponentRegistry.get(c.type);
        if (!fc || typeof fc.renderPreview !== 'function') return null;
        return (
          <Box key={c.id} sx={{ mb: 1 }}>
            {fc.renderPreview(c.properties || {}, { component: c, canSubmit })}
          </Box>
        );
      })}
    </Box>
  </Box>
);

/**
 * A small, static, no-Meta preview of a flow's first screen, rendered straight from its flowJson
 * with the same component registry the builder uses. Works for local-only drafts (no
 * whatsapp_flow_id) since it never calls Meta. The card is scaled + clipped + pointer-events:none so
 * it reads as a thumbnail, not an interactive surface; one misbehaving component can't crash the card.
 *
 * `variant`:
 *  - `'card'` (default), the 150px flows-list thumbnail. Caps at MAX_COMPONENTS, but the Footer
 *    (always the compiler-added last child) is kept no matter what gets trimmed to make room.
 *  - `'full'`, the assistant's larger preview. Shows every component with no cap; the frame
 *    scrolls instead of clipping, so nothing, including the Footer, is ever hidden.
 */
const FlowThumbnail = ({ flowJson, height = 172, accent = '#1B8F47', screenIndex = 0, variant = 'card' }) => {
  // Defaults to the first screen, which is all the flows list ever wants. The AI builder pages
  // through them, because a flow the model just wrote is usually more than one screen and
  // showing only the first hides most of what it did.
  const { title, components, canSubmit } = React.useMemo(
    () => screenComponents(flowJson, screenIndex, variant), [flowJson, screenIndex, variant]);
  const scrollable = variant === 'full';
  const screenCount = flowJson?.screens?.length || 1;
  const progress = Math.min(1, (screenIndex + 1) / screenCount);

  return (
    <Box
      sx={{
        position: 'relative',
        height,
        overflow: 'hidden',
        bgcolor: '#fff',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* WhatsApp's sheet header: close, the screen's title centred, the menu, and a bar showing
          how far through the flow this screen is. It used to be a green bar, which is how no
          WhatsApp version draws a flow (compared with a real phone on 2026-10-10). */}
      <Box sx={{ height: HEADER_H, bgcolor: '#fff', color: 'text.primary', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 0.75, gap: 0.5, minWidth: 0 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
          <Typography noWrap sx={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 12, fontWeight: 600 }}>
            {title || 'Untitled screen'}
          </Typography>
          <MoreVertIcon sx={{ fontSize: 14 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: '3px', px: 1.25, pb: '6px' }} data-testid="thumbnail-progress">
          <Box sx={{ flex: progress, height: 2.5, borderRadius: 2, bgcolor: accent }} />
          {progress < 1 && <Box sx={{ flex: 1 - progress, height: 2.5, borderRadius: 2, bgcolor: '#ECEAE6' }} />}
        </Box>
      </Box>

      {components.length > 0 ? (
        <ErrorBoundary fallback={() => <EmptyState label="Preview unavailable" />}>
          <PreviewBody components={components} scrollable={scrollable} canSubmit={canSubmit} />
        </ErrorBoundary>
      ) : (
        <EmptyState label="No components yet" />
      )}

      {/* fade so clipped content tails off cleanly, not for the scrollable full preview, where
          it would permanently obscure whatever ended up at the bottom. */}
      {!scrollable && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 44,
            background: 'linear-gradient(rgba(255,255,255,0), #fff)',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
};

export default React.memo(FlowThumbnail);
